import React from 'react';
import { Card, CardContent } from '../../../../components/card';
import TrendingUpIcon from '../../../../assets/icons/trending-up-icon.svg';
import { NeighborhoodAnalysis } from '../MerchantHeatmap.data';
import { PERFORMANCE_LABELS, PERFORMANCE_COLORS } from '../MerchantHeatmap.const';

interface NeighborhoodTableProps {
    data: NeighborhoodAnalysis[];
}

export const NeighborhoodTable: React.FC<NeighborhoodTableProps> = ({ data }) => {
    return (
        <Card className="bg-white dark:bg-gray-800 border border-[#e6e6e6] dark:border-gray-700 rounded-[12.13px]">
            <CardContent className="p-[1.6px]">
                <div className="flex flex-col gap-[4.87px] pb-0 pt-[19.5px] px-[19.5px]">
                    <h4 className="text-[15px] font-semibold text-[#364153] dark:text-white leading-[13px]" style={{ fontFamily: 'Nunito' }}>
                        Neighborhood Analysis & Insights
                    </h4>
                    <p className="text-[13px] font-normal text-[#4a5565] dark:text-gray-400 leading-[19.5px]" style={{ fontFamily: 'Nunito' }}>
                        Detailed breakdown of coupon redemptions across Fairburn districts with performance metrics
                    </p>
                </div>

                <div className="flex flex-col items-start justify-center overflow-auto">
                    {/* Table Header */}
                    <div className="bg-[#f9fafb] dark:bg-gray-700 border-b border-[rgba(0,0,0,0.1)] dark:border-gray-600 h-[32.5px] flex items-start justify-center pb-[0.8px] pt-0 px-[15px] w-full">
                        <div className="flex-1 flex flex-col h-[32.5px] items-start pb-[8.47px] pt-[7.03px] px-[6.5px]">
                            <div className="text-[11.4px] font-semibold text-[#364153] dark:text-gray-300 leading-[16.25px]" style={{ fontFamily: 'Nunito' }}>
                                Area Name
                            </div>
                        </div>
                        <div className="flex flex-col h-[32.5px] items-center pb-[8.47px] pt-[7.03px] px-[6.5px] w-[113.23px]">
                            <div className="text-[11.4px] font-semibold text-[#364153] dark:text-gray-300 text-center leading-[16.25px]" style={{ fontFamily: 'Nunito' }}>
                                Total Redemptions
                            </div>
                        </div>
                        <div className="flex flex-col h-[32.5px] items-center pb-[8.47px] pt-[7.03px] px-[6.5px] w-[169.24px]">
                            <div className="text-[11.4px] font-semibold text-[#364153] dark:text-gray-300 text-center leading-[16.25px]" style={{ fontFamily: 'Nunito' }}>
                                % of Total
                            </div>
                        </div>
                        <div className="flex flex-col h-[32.5px] items-center pb-[8.47px] pt-[7.03px] px-[6.5px] w-[144.09px]">
                            <div className="text-[11.4px] font-semibold text-[#364153] dark:text-gray-300 text-center leading-[16.25px]" style={{ fontFamily: 'Nunito' }}>
                                Top Coupon
                            </div>
                        </div>
                        <div className="flex flex-col h-[32.5px] items-center pb-[8.47px] pt-[7.03px] px-[6.5px] w-[114.19px]">
                            <div className="text-[11.4px] font-semibold text-[#364153] dark:text-gray-300 text-center leading-[16.25px]" style={{ fontFamily: 'Nunito' }}>
                                Performance
                            </div>
                        </div>
                    </div>

                    {/* Table Body */}
                    <div className="flex flex-col items-start w-full">
                        {data.map((row, index) => (
                            <div
                                key={index}
                                className="border-b border-[rgba(0,0,0,0.1)] dark:border-gray-600 h-[39.8px] flex items-start justify-center pb-[0.8px] pt-0 px-[15px] w-full hover:bg-gray-50 dark:hover:bg-gray-700/50"
                            >
                                <div className="flex-1 flex flex-col h-[39.8px] items-start pb-[11.77px] pt-[10.88px] px-[6.5px]">
                                    <div className="text-[11.4px] font-medium text-[#4a5565] dark:text-gray-400 leading-[16.25px]" style={{ fontFamily: 'Nunito' }}>
                                        {row.area_name}
                                    </div>
                                </div>
                                <div className="flex flex-col h-[39.8px] items-center pb-[11.92px] pt-[10.88px] px-[6.5px] w-[113.23px]">
                                    <div className="text-[11.4px] font-medium text-[#364153] dark:text-gray-300 text-center leading-[16.25px]" style={{ fontFamily: 'Nunito' }}>
                                        {row.total_redemptions}
                                    </div>
                                </div>
                                <div className="flex flex-col h-[39.8px] items-start justify-center p-[6.5px] w-[169.24px]">
                                    {row.percentage_of_total > 0 ? (
                                        <div className="flex gap-[5px] items-center justify-center w-full">
                                            <div className="text-[11.4px] font-medium text-[#364153] dark:text-gray-300 text-center leading-[16.25px]" style={{ fontFamily: 'Nunito' }}>
                                                +{row.percentage_of_total.toFixed(1)}%
                                            </div>
                                            <img src={TrendingUpIcon} alt="Trending Up" className="w-[13px] h-[13px]" />
                                        </div>
                                    ) : (
                                        <div className="flex flex-col gap-0 items-center w-full">
                                            <div className="text-[11.4px] font-medium text-[#364153] dark:text-gray-300 text-center leading-[16.25px]" style={{ fontFamily: 'Nunito' }}>
                                                {row.percentage_of_total.toFixed(1)}%
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <div className="flex flex-col h-[39.8px] items-center pb-[11.92px] pt-[10.88px] px-[6.5px] w-[144.09px]">
                                    <div className="text-[11.4px] font-semibold text-[#364153] dark:text-gray-300 text-center leading-[16.25px]" style={{ fontFamily: 'Nunito' }}>
                                        {row.top_coupon}
                                    </div>
                                </div>
                                <div className="flex flex-col h-[39.8px] items-center justify-center pb-[11.77px] pt-[10.88px] px-[6.5px] w-[114.19px]">
                                    <div
                                        className="flex items-center px-[6px] py-[2px] rounded-full"
                                        style={{
                                            backgroundColor: PERFORMANCE_COLORS[row.performance].bg,
                                            color: PERFORMANCE_COLORS[row.performance].text,
                                            fontFamily: 'Nunito',
                                        }}
                                    >
                                        <span className="text-[11px] font-medium leading-[16px]">
                                            {PERFORMANCE_LABELS[row.performance]}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </CardContent>
        </Card>
    );
};

